import React, { useState } from 'react';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  Globe,
  Key,
  Loader2,
  LogIn,
  LogOut,
  Mail,
  Monitor,
  Settings,
  Shield,
  User,
} from 'lucide-react';
import { useGetActivityLogQuery, ActivityLogEntry } from '../../services/profileApi';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'profile', label: 'Profile' },
  { value: 'security', label: 'Security' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'settings', label: 'Settings' },
];

const ProfileActivity: React.FC = () => {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const limit = 10;

  const {
    data: activityData,
    isLoading,
    error,
    isFetching,
  } = useGetActivityLogQuery({ page, limit, category: category || undefined });

  const activities = activityData?.activities || [];
  const pagination = activityData?.pagination;

  const getActivityIcon = (action: string, category: string) => {
    const iconClass = 'w-4 h-4';

    if (action.includes('login')) return <LogIn className={`${iconClass} text-green-600`} />;
    if (action.includes('logout')) return <LogOut className={`${iconClass} text-gray-600`} />;
    if (action.includes('password')) return <Key className={`${iconClass} text-amber-600`} />;
    if (action.includes('email')) return <Mail className={`${iconClass} text-blue-600`} />;
    if (action.includes('avatar')) return <User className={`${iconClass} text-purple-600`} />;

    switch (category) {
      case 'profile':
        return <User className={`${iconClass} text-brand-primary`} />;
      case 'security':
        return <Shield className={`${iconClass} text-amber-600`} />;
      case 'authentication':
        return <Key className={`${iconClass} text-green-600`} />;
      case 'settings':
        return <Settings className={`${iconClass} text-gray-600`} />;
      default:
        return <Activity className={`${iconClass} text-gray-500`} />;
    }
  };

  const getStatusBadge = (status: 'success' | 'failed' | 'pending') => {
    switch (status) {
      case 'success':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
            Success
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
            Failed
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
            Pending
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return formatDate(dateString);
  };

  const groupActivitiesByDate = (activities: ActivityLogEntry[]) => {
    const groups: { [key: string]: ActivityLogEntry[] } = {};

    activities.forEach(activity => {
      const date = new Date(activity.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(activity);
    });

    return groups;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load activity log. Please try again.
        </div>
      </div>
    );
  }

  const groupedActivities = groupActivitiesByDate(activities);

  return (
    <div className="p-5 max-w-4xl">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Activity Log</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          View your account activity and security events
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter by:</span>
          </div>
          <select
            value={category}
            onChange={e => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            {CATEGORY_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Clock className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Recent Activity</h2>
            <p className="text-xs text-gray-500">{pagination?.total || 0} total activities</p>
          </div>
        </div>

        {activities.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Activity className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No Activity Yet</h3>
            <p className="text-sm text-gray-500">Your account activity will appear here.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedActivities).map(([date, dayActivities]) => (
              <div key={date}>
                {/* Date Header */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {date}
                  </span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                {/* Activities for this date */}
                <div className="space-y-4">
                  {dayActivities.map(activity => (
                    <ActivityItem
                      key={activity._id}
                      activity={activity}
                      getActivityIcon={getActivityIcon}
                      getStatusBadge={getStatusBadge}
                      getRelativeTime={getRelativeTime}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, pagination.total)} of{' '}
                {pagination.total} activities
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isFetching}
                  className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-3 py-1 text-sm font-medium text-gray-700">
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages || isFetching}
                  className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-5">
        <div className="flex items-center space-x-2 mb-3">
          <Shield className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">Activity Monitoring</h3>
        </div>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>We log all account activity for security purposes.</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>If you see any suspicious activity, change your password immediately.</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Activity logs are retained for 90 days.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Activity Item Component
interface ActivityItemProps {
  activity: ActivityLogEntry;
  getActivityIcon: (action: string, category: string) => React.ReactNode;
  getStatusBadge: (status: 'success' | 'failed' | 'pending') => React.ReactNode;
  getRelativeTime: (dateString: string) => string;
}

const ActivityItem: React.FC<ActivityItemProps> = ({
  activity,
  getActivityIcon,
  getStatusBadge,
  getRelativeTime,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-white rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
            {getActivityIcon(activity.action, activity.category)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">{activity.description}</p>
            <div className="flex items-center space-x-3 mt-1">
              <span className="text-xs text-gray-500">{getRelativeTime(activity.createdAt)}</span>
              <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded capitalize">
                {activity.category}
              </span>
            </div>
          </div>
        </div>
        {getStatusBadge(activity.status)}
      </div>

      {/* Expanded Details */}
      {expanded && activity.metadata && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {activity.metadata.ipAddress && (
              <div className="flex items-center space-x-2">
                <Globe className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-500">IP Address:</span>
                <span className="text-gray-700 font-medium">{activity.metadata.ipAddress}</span>
              </div>
            )}
            {activity.metadata.device && (
              <div className="flex items-center space-x-2">
                <Monitor className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-500">Device:</span>
                <span className="text-gray-700 font-medium">{activity.metadata.device}</span>
              </div>
            )}
            {activity.metadata.location && (
              <div className="flex items-center space-x-2 col-span-2">
                <Globe className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-500">Location:</span>
                <span className="text-gray-700 font-medium">
                  {[activity.metadata.location.city, activity.metadata.location.country]
                    .filter(Boolean)
                    .join(', ') || 'Unknown'}
                </span>
              </div>
            )}
            {activity.metadata.changedFields && activity.metadata.changedFields.length > 0 && (
              <div className="col-span-2">
                <span className="text-gray-500">Changed fields: </span>
                <span className="text-gray-700">{activity.metadata.changedFields.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileActivity;
